import { Grid, Theme, useTheme } from "@mui/material";
import {
  CAPACITY_STATUS_FREE,
  CAPACITY_STATUS_FULL,
  CAPACITY_STATUS_UNKNOWN,
  TRACK_EVENTS,
} from "core/consts";
import { useDateLocale } from "core/hooks/useDateLocale";
import { convertMetertoKm } from "core/model/utils/location";
import { ONTOLOGIES } from "core/model/utils/ontologies";
import { useGetOntology } from "core/model/utils/ontologies/hooks";
import { grammaticalList } from "core/model/utils/strings";
import { CapacityStatus, ProviderListing } from "core/types";
import { format, fromUnixTime } from "date-fns";
import Tooltip from "ds_legacy/components/Tooltip";
import { KEYBOARD_FOCUS_OUTLINE } from "ds_legacy/materials/colors";
import { VerticalLayout } from "ds_legacy/materials/layouts";
import { dp, margin, padding, sizing } from "ds_legacy/materials/metrics";
import { SHADOW_DP5 } from "ds_legacy/materials/shadows";
import {
  Body,
  FONT_SIZE_14,
  FONT_SIZE_18,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_REGULAR,
  LINE_HEIGHT_20,
  Title,
} from "ds_legacy/materials/typography";
import Image from "dsl/atoms/Image";
import { useHasOverFlown } from "dsl/hooks";
import { useProvidersearchNavigationUrls } from "dsl/hooks/useNavigationHandlers";
import { CSSProperties } from "react";
import { Link } from "react-router-dom";
import { useTracking } from "react-tracking";
import styled from "styled-components";
import { useTranslations } from "translations";
import Translations from "translations/types";

const PROVIDER_CARD_HEIGHT = dp(200);
const CONSULTANT_CARD_HEIGHT = sizing(16);

const ProviderTitle = styled(Title)`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 1;
  display: -webkit-box;
  font-size: ${FONT_SIZE_18};
  margin: ${margin(0)};
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  word-wrap: break-word;
`;

const StyledLink = styled(Link)`
  color: inherit;
  text-decoration: none;
  &:focus {
    outline: none;
  }
  &:after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    right: 0;
    bottom: 0;
  }
`;

const ProviderSearchCardWrapper = styled.div<{
  $isConsultant?: boolean;
  borderColor: string;
}>`
  background-color: ${({ $isConsultant, theme }) =>
    $isConsultant ? (theme as any).palette.neutrals.custom_white : ""};
  border-color: ${(props) => (props.theme as any).palette.neutrals.medium_grey};
  border-radius: ${dp(8)};
  border-right-color: ${(props) => props.borderColor};
  border-style: solid;
  border-width: ${dp(1)} ${dp(8)} ${dp(1)} ${dp(1)};
  color: inherit;
  cursor: pointer;
  display: flex;
  list-style: none;
  margin: ${margin(0, 0, 2)};
  min-height: ${({ $isConsultant }) =>
    $isConsultant ? CONSULTANT_CARD_HEIGHT : PROVIDER_CARD_HEIGHT};
  overflow-x: auto;
  position: relative;
  text-decoration: none;

  &:hover {
    box-shadow: ${SHADOW_DP5};
  }
  &:focus-within {
    outline: ${dp(2)} solid ${KEYBOARD_FOCUS_OUTLINE};
  }
`;

export const getCardConfig = (
  theme: Theme,
  translations: Translations,
  capacity?: CapacityStatus,
  is_consultant?: boolean,
): {
  color: string;
  fontWeight?: CSSProperties["fontWeight"];
  icon: {
    alt: string;
    path: string;
  };
  label: string;
} => {
  if (is_consultant) {
    return {
      color: (theme as any).palette.neutrals.custom_blue,
      icon: {
        alt: translations.providersearch.map.legendConsultant,
        path: "info_center_x2.png",
      },
      label:
        translations.providersearch.listingPage.consultantCard
          .appointmentInformation,
    };
  }

  switch (capacity) {
    case CAPACITY_STATUS_FREE:
      return {
        color: theme.palette.success.main,
        fontWeight: FONT_WEIGHT_BOLD,
        icon: {
          alt: translations.providersearch.map.legendProvider,
          path: "care_provider_x2.png",
        },
        label:
          translations.providersearch.listingPage.providerCard.capacity
            .freeCapacity,
      };
    case CAPACITY_STATUS_FULL:
      return {
        color: theme.palette.error.main,
        fontWeight: FONT_WEIGHT_BOLD,
        icon: {
          alt: translations.providersearch.map.legendNotAvailable,
          path: "not_available_x2.png",
        },
        label:
          translations.providersearch.listingPage.providerCard.capacity
            .noCapacity,
      };
    case CAPACITY_STATUS_UNKNOWN:
      return {
        color: theme.palette.warning.main,
        fontWeight: FONT_WEIGHT_BOLD,
        icon: {
          alt: translations.providersearch.map.legendProvider,
          path: "care_provider_x2.png",
        },
        label:
          translations.providersearch.listingPage.providerCard.capacity
            .capacityUnknown,
      };
    default:
      return {
        color: theme.palette.neutrals.medium_grey,
        icon: {
          alt: translations.providersearch.map.legendProvider,
          path: "care_provider_x2.png",
        },
        label: "",
      };
  }
};

const ImageSection = ({
  isConsultant,
  thumbnailUrl,
}: {
  isConsultant?: boolean;
  thumbnailUrl: string | undefined;
}) => {
  if (isConsultant) return null;

  return (
    <Grid
      display={{ desktop: "block", xs: "none" }}
      height={PROVIDER_CARD_HEIGHT}
      item
      maxWidth={sizing(30)}
      minWidth={sizing(30)}
    >
      <img
        alt="" // hide from screenreaders
        data-testid="provider-image"
        src={thumbnailUrl}
        style={{ height: "100%", width: "100%" }}
      />
    </Grid>
  );
};

const DistanceZipcodeInfo = ({
  city,
  distance,
  zipcode,
}: {
  city: string | undefined;
  distance: number | undefined;
  zipcode: string | undefined;
}) => {
  const translations = useTranslations();
  const theme = useTheme();
  const distanceInKm = convertMetertoKm(distance ?? 0);
  const distanceLabel = `, ${distanceInKm} km ${translations.bToC.providerList.away}`;

  if (!city || !zipcode) return null;

  return (
    <Body
      as="p"
      color={theme.palette.neutrals.dark_grey}
      data-testid="provider-location"
      fontSize={FONT_SIZE_14}
      fontWeight={FONT_WEIGHT_BOLD}
      margin={margin(0)}
    >
      {distanceInKm
        ? `${zipcode} ${city}${distanceLabel}`
        : `${translations.providersearch.providerDetailsPage.contactSection.addressDistanceZero(
            { zipcode },
          )}, ${city}`}
    </Body>
  );
};

const ProviderContentTitle = ({
  careprovider,
}: {
  careprovider: ProviderListing;
}) => {
  const theme = useTheme();
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const { hasOverFlown, ref } = useHasOverFlown<HTMLHeadingElement>({
    direction: "y",
    value: careprovider.name,
  });
  const { goToConsultant, goToProvider } = useProvidersearchNavigationUrls();

  return (
    <>
      <Tooltip title={hasOverFlown ? careprovider.name : ""}>
        <ProviderTitle
          ref={ref}
          as="h3"
          bold
          color={theme.palette.neutrals.base_dark}
          data-testid="provider-name"
        >
          <StyledLink
            to={
              careprovider.is_consultant
                ? goToConsultant({ consultantId: careprovider.id })
                : goToProvider({ careproviderId: careprovider.id })
            }
            onClick={() =>
              trackEvent({
                name: careprovider.is_consultant
                  ? TRACK_EVENTS.PROVIDER_SEARCH_CONSULTANT_CARD_CLICKED
                  : TRACK_EVENTS.PROVIDER_SEARCH_PROVIDER_CARD_CLICKED,
              })
            }
          >
            {careprovider.name}
          </StyledLink>
        </ProviderTitle>
      </Tooltip>
      {careprovider.project_sponsor && (
        <Body
          as="p"
          fontSize={FONT_SIZE_14}
          fontWeight={FONT_WEIGHT_BOLD}
          margin={margin(0)}
        >
          {translations.providersearch.listingPage.consultantCard.agency({
            sponsorName: careprovider.project_sponsor,
          })}
        </Body>
      )}
      <DistanceZipcodeInfo
        city={careprovider.address?.city}
        distance={careprovider.distance}
        zipcode={careprovider.address?.zip_code}
      />
    </>
  );
};

const MAX_FACILITIES_TO_SHOW = 3;

const ProviderFacilitiesSection = ({
  facilities,
}: {
  facilities: Array<number> | undefined;
}) => {
  const theme = useTheme();
  const getOntology = useGetOntology();
  const translations = useTranslations();
  const itemsToShow = facilities?.slice(0, MAX_FACILITIES_TO_SHOW);
  let string = "";

  if (facilities && facilities?.length > MAX_FACILITIES_TO_SHOW) {
    string = `${itemsToShow
      ?.map((item) => getOntology({ key: item, type: ONTOLOGIES.FACILITY }))
      .join(", ")} ${
      translations.providersearch.listingPage.providerCard.moreProviders
    }`;
  } else {
    string = grammaticalList({
      array: itemsToShow,
      getOntology,
      ontologyType: ONTOLOGIES.FACILITY,
      translations,
    });
  }

  return string ? (
    <Body
      as="p"
      breakWord
      color={theme.palette.neutrals.base_dark}
      data-testid="provider-facility"
      fontSize={FONT_SIZE_14}
      fontWeight={FONT_WEIGHT_REGULAR}
      lineHeight={LINE_HEIGHT_20}
      margin={margin(0)}
      maxWidth="100%"
    >
      {string}
    </Body>
  ) : null;
};

const ProviderCapacityStatus = ({
  capacityStatus,
  isConsultant,
  updatedAt,
}: {
  capacityStatus?: CapacityStatus;
  isConsultant?: boolean;
  updatedAt?: number | null;
}) => {
  const { currentLocale: locale } = useDateLocale();
  const theme = useTheme();
  const translations = useTranslations();
  const { fontWeight, icon, label } = getCardConfig(
    theme,
    translations,
    capacityStatus,
    isConsultant,
  );
  const capacityUpdatedAtLabel = updatedAt
    ? translations.providersearch.listingPage.providerCard.capacity.capacityUpdatedAt(
        { updatedAt: format(fromUnixTime(updatedAt), "P", { locale }) },
      )
    : "";

  return (
    <>
      <VerticalLayout
        margin={margin(0, 1, 0, 0)}
        overflow="visible"
        style={{ width: `calc(100% - ${dp(32)})` }}
      >
        <Body
          as="p"
          breakWord
          color={theme.palette.neutrals.base_dark}
          data-testid="provider-capacity"
          fontSize={FONT_SIZE_14}
          fontWeight={fontWeight}
          lineHeight={LINE_HEIGHT_20}
          margin={margin(0, 1, 0, 0)}
          style={{ whiteSpace: "normal" }}
          textAlign="end"
          width="100%"
        >
          {label}
        </Body>
        {capacityUpdatedAtLabel && (
          <Body
            as="p"
            breakWord
            color={theme.palette.neutrals.dark_grey}
            fontSize={FONT_SIZE_14}
            lineHeight={LINE_HEIGHT_20}
            margin={margin(0, 1, 0, 0)}
            textAlign="end"
            width="100%"
          >
            {capacityUpdatedAtLabel}
          </Body>
        )}
      </VerticalLayout>
      <Image alt={icon.alt} path={icon.path} style={{ width: dp(24) }} />
    </>
  );
};

const ProviderContentSection = ({
  careprovider,
}: {
  careprovider: ProviderListing;
}) => {
  return (
    <Grid
      container
      direction="column"
      item
      padding={padding(2, 1.5)}
      wrap="nowrap"
    >
      <Grid item xs={12}>
        <ProviderContentTitle careprovider={careprovider} />
      </Grid>
      <Grid columnGap={4} container item justifyContent="space-between" xs={12}>
        <Grid alignItems="flex-end" container item sm xs={12}>
          <ProviderFacilitiesSection facilities={careprovider.facilities} />
        </Grid>
        <Grid
          alignItems="flex-center"
          container
          display="flex"
          flexWrap="nowrap"
          item
          justifyContent="flex-end"
          minWidth={dp(200)}
          sm={4}
          sx={{ marginTop: "auto" }}
          xs={12}
        >
          <ProviderCapacityStatus
            capacityStatus={careprovider.capacity?.capacity_status}
            isConsultant={careprovider.is_consultant}
            updatedAt={careprovider.capacity?.updated_at}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

export const ProviderSearchCard = ({
  careprovider,
}: {
  careprovider: ProviderListing;
}) => {
  const theme = useTheme();
  const translations = useTranslations();

  const { color } = getCardConfig(
    theme,
    translations,
    careprovider.capacity?.capacity_status,
    careprovider.is_consultant,
  );

  return (
    <ProviderSearchCardWrapper
      $isConsultant={careprovider.is_consultant}
      borderColor={color}
      data-testid={`provider-card-${careprovider.id}`}
    >
      <Grid container wrap="nowrap">
        <ImageSection
          isConsultant={careprovider.is_consultant}
          thumbnailUrl={careprovider.thumbnail_url}
        />
        <ProviderContentSection careprovider={careprovider} />
      </Grid>
    </ProviderSearchCardWrapper>
  );
};
